import { useAppDispatch } from '@store/hooks';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import { Form } from 'react-bootstrap';
import { AddGlDailyRates, GetGlDailyRatesList } from '@store/Finance/DailyRates/DailyRatesSlice';
import useLookupCurrency from '@hooks/Lookups/use-Lookup-Currency';
import { DailyRatesSchema } from '../../../ValidationForm/validationSchema';
 

interface FormValues {
  id: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  conversionStartDate: string;
  conversionRate: string;
}

interface CurrencyOption {
  currencyCode: string;
  description: string;
}

const AddDailyRates: React.FC = () => {
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { Currency } = useLookupCurrency();

  const formik = useFormik<FormValues>({
    initialValues: {
      id: '',
      fromCurrencyCode: '',
      toCurrencyCode: '',
      conversionStartDate: '',
      conversionRate: ''
    },
    validationSchema: DailyRatesSchema(t),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      dispatch(
        AddGlDailyRates({
          fromCurrencyCode: values.fromCurrencyCode,
          toCurrencyCode: values.toCurrencyCode,
          conversionStartDate: values.conversionStartDate,
          conversionRate: parseFloat(values.conversionRate)
        })
      )
        .unwrap()
        .then((res: any) => {
          if (res?.succeeded === true) {
            CustomAlert({ action: 'Add' });
            document.getElementById('closeModalForm')?.click();
            resetForm();
            dispatch(GetGlDailyRatesList());
          } else {
            setSubmitting(false);
            CustomAlert({ action: 'Error', msg: res?.message });
          }
        })
        .catch((error: any) => {
          setSubmitting(false);
          CustomAlert({ action: 'Error' });
        });
    }
  });

  // التحقق من أن العملتين مختلفتين
  const isSameCurrency =
    formik.values.fromCurrencyCode && formik.values.fromCurrencyCode === formik.values.toCurrencyCode;

  // الحصول على التاريخ الحالي لتحديد الحد الأدنى للتاريخ
  const getTodayDate = (): string => {
    return new Date().toISOString().split('T')[0];
  };

  // معالجة تغيير العملة المصدر
  const handleFromCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formik.handleChange(e);
    // إذا كانت العملتان متشابهتين، إعادة تعيين العملة الهدف
    if (e.target.value === formik.values.toCurrencyCode) {
      formik.setFieldValue('toCurrencyCode', '');
    }
  };

  // معالجة تغيير العملة الهدف
  const handleToCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    formik.handleChange(e);
    // إذا كانت العملتان متشابهتين، إعادة تعيين العملة المصدر
    if (e.target.value === formik.values.fromCurrencyCode) {
      formik.setFieldValue('fromCurrencyCode', '');
    }
  };

  return (
    <div>
      <Form onSubmit={formik.handleSubmit}>
        <div className='row'>
          {/* العملة المصدر */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>{i18n.language === 'ar' ? 'من العملة' : 'From Currency'}</label>
              <Form.Select
                className='form-select'
                name='fromCurrencyCode'
                onChange={handleFromCurrencyChange}
                value={formik.values.fromCurrencyCode}
                isInvalid={!!(formik.touched.fromCurrencyCode && formik.errors.fromCurrencyCode)}
              >
                <option value=''>{i18n.language === 'ar' ? 'إختر العملة المصدر' : 'Choose Source Currency'}</option>
                {Currency?.map((item: CurrencyOption, idx: number) => (
                  <option
                    className={item.currencyCode === formik.values.toCurrencyCode ? 'text-danger opacity-50' : ''}
                    key={item.currencyCode}
                    value={item.currencyCode}
                    disabled={item.currencyCode === formik.values.toCurrencyCode}
                  >
                    {item.currencyCode} - {item.description}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type='invalid'>{formik.errors.fromCurrencyCode}</Form.Control.Feedback>
            </div>
          </div>

          {/* العملة الهدف */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>{i18n.language === 'ar' ? 'إلى العملة' : 'To Currency'}</label>
              <Form.Select
                className='form-select'
                name='toCurrencyCode'
                onChange={handleToCurrencyChange}
                value={formik.values.toCurrencyCode}
                isInvalid={!!(formik.touched.toCurrencyCode && formik.errors.toCurrencyCode)}
              >
                <option value=''>{i18n.language === 'ar' ? 'إختر العملة الهدف' : 'Choose Target Currency'}</option>
                {Currency?.map((item: CurrencyOption, idx: number) => (
                  <option
                    className={item.currencyCode === formik.values.fromCurrencyCode ? 'text-danger opacity-50' : ''}
                    key={item.currencyCode}
                    value={item.currencyCode}
                    disabled={item.currencyCode === formik.values.fromCurrencyCode}
                  >
                    {item.currencyCode} - {item.description}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type='invalid'>{formik.errors.toCurrencyCode}</Form.Control.Feedback>
            </div>
          </div>

          {/* سعر التحويل */}
          <div className='col-xl-6 '>
            <div className='form-group mb-3'>
              <label className='form-label required'>
                {i18n.language === 'ar' ? 'سعر التحويل' : 'Conversion Rate'}
              </label>
              <Form.Control
                className='form-control'
                type='number'
                step='0.0001'
                min='0'
                autoComplete='off'
                placeholder={i18n.language === 'ar' ? 'أدخل سعر التحويل' : 'Enter conversion rate'}
                name='conversionRate'
                onChange={formik.handleChange}
                value={formik.values.conversionRate}
                isInvalid={!!(formik.touched.conversionRate && formik.errors.conversionRate)}
              />
              <Form.Text className='text-muted'>
                {i18n.language === 'ar' ? 'يجب أن يكون السعر أكبر من الصفر' : 'Rate must be greater than zero'}
              </Form.Text>
              <Form.Control.Feedback type='invalid'>{formik.errors.conversionRate}</Form.Control.Feedback>
            </div>
          </div>

          {/* تاريخ البدء */}
          <div className='col-xl-6'>
            <div className='form-group mb-3'>
              <label className='form-label required'>{i18n.language === 'ar' ? 'تاريخ البدء' : 'Start Date'}</label>
              <Form.Control
                className='form-control'
                type='date'
                min={getTodayDate()}
                name='conversionStartDate'
                onChange={formik.handleChange}
                value={formik.values.conversionStartDate}
                isInvalid={!!(formik.touched.conversionStartDate && formik.errors.conversionStartDate)}
              />
              <Form.Text className='text-muted'>
                {i18n.language === 'ar' ? 'لا يمكن اختيار تاريخ ماضي' : 'Cannot select past date'}
              </Form.Text>
              <Form.Control.Feedback type='invalid'>{formik.errors.conversionStartDate}</Form.Control.Feedback>
            </div>
          </div>
        </div>

        {/* تحذير إذا كانت العملتان متشابهتين */}
        {isSameCurrency && (
          <div className='alert alert-warning mb-3'>
            <i className='bi bi-exclamation-triangle me-2'></i>
            {i18n.language === 'ar'
              ? 'لا يمكن اختيار نفس العملة للمصدر والهدف'
              : 'Cannot select the same currency for source and target'}
          </div>
        )}

        {/* معاينة السعر */}
        {/* {formik.values.fromCurrencyCode && formik.values.toCurrencyCode && formik.values.conversionRate && (
          <div className='alert alert-info mb-3'>
            <div className='d-flex align-items-center'>
              <i className='bi bi-info-circle me-2'></i>
              <span>{i18n.language === 'ar' ? 'معاينة السعر:' : 'Rate Preview:'}</span>
            </div>
            <div className='mt-2 text-center'>
              <span className='badge bg-primary fs-6'>{formik.values.fromCurrencyCode}</span>
              <i className='bi bi-arrow-right mx-3 text-theme'></i>
              <span className='badge bg-secondary fs-6'>{formik.values.toCurrencyCode}</span>
              <div className='mt-2'>
                <strong className='text-success fs-5'>
                  1 {formik.values.fromCurrencyCode} = {parseFloat(formik.values.conversionRate).toFixed(4)}{' '}
                  {formik.values.toCurrencyCode}
                </strong>
              </div>
            </div>
          </div>
        )} */}

        {formik.values.fromCurrencyCode && formik.values.toCurrencyCode && formik.values.conversionRate && (
          <div className='alert alert-info mb-3'>
            <div className='d-flex align-items-center'>
              <i className='bi bi-info-circle me-2'></i>
              <span>{i18n.language === 'ar' ? 'معاينة السعر:' : 'Rate Preview:'}</span>
            </div>
            <div className='mt-2 text-center'>
              {/* ✅ تم تصحيح الاتجاه */}
              {/* <span className='badge bg-secondary fs-6'>{formik.values.fromCurrencyCode}</span>
              <i className='bi bi-arrow-right mx-3 text-theme'></i>
              <span className='badge bg-primary fs-6'>{formik.values.toCurrencyCode}</span>

              */}

              <div className='mt-3 d-flex justify-content-center align-items-center gap-3 flex-wrap'>
                {/* العملة المصدر */}
                <div className='d-flex flex-column align-items-center'>
                  <span className='fw-bold text-muted'>1</span>
                  <span className='badge bg-secondary fs-6'>{formik.values.fromCurrencyCode}</span>
                </div>

                {/* السهم */}
                {/* <i className='bi bi-arrow-right fs-4 text-theme'></i> */}
                {/* <i className="fas fa-exchange-alt mx-2 text-muted"></i> */}
                <i className="bi bi-arrow-left-right text-secondary fs-5"></i>


                {/* العملة الهدف */}
                <div className='d-flex flex-column align-items-center'>
                  <span className='fw-bold text-success'>{parseFloat(formik.values.conversionRate).toFixed(4)}</span>
                  <span className='badge bg-primary fs-6'>{formik.values.toCurrencyCode}</span>
                </div>
              </div>


              
            </div>
          </div>
        )}

        {/* أزرار الإجراءات */}
        <div className='text-center mt-4'>
          <div className='d-flex justify-content-between gap-3'>
            <button
              type='submit'
              className='btn btn-lg btn-theme mb-1 w-100 rounded-1'
              disabled={formik.isSubmitting || !formik.isValid || isSameCurrency}
            >
              {formik.isSubmitting ? (
                <>
                  <div className='spinner-border spinner-border-sm me-2'></div>
                  {i18n.language === 'ar' ? 'جاري الإضافة...' : 'Adding...'}
                </>
              ) : (
                <>
                  <i className='fas fa-plus me-2'></i>
                  {t('Add')}
                </>
              )}
            </button>

            <button
              type='button'
              className='btn btn-lg btn-outline-secondary mb-1 w-100 rounded-1'
              data-bs-dismiss='modal'
              id='closeModalForm'
              disabled={formik.isSubmitting}
            >
              <i className='fas fa-times me-2'></i>
              {t('Cancel')}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default AddDailyRates;
